import { IonImg, IonPage, useIonToast } from '@ionic/react';
import { Business, EmptyBusiness } from 'model';
import React, { useCallback, useEffect, useState } from 'react';
import { useLazyGetBusinessImagesQuery } from '../api/BusinessApi.js';
import { BusinessWithSimpleItems } from '../formConfigs/EditBusinessFieldsConfig';
import { FormValuesType } from '../hooks/useFormValues';
import { useLoading } from '../hooks/useQuirkLoading';
import styles from './BusinessImageLookup.module.css';
import { Header } from './Header';
import { IconButton } from './IconButton';
import { IconClose, IconSadFace } from './QuirkIcons';
import { QuirkIonContent } from './QuirkIonContent';

export const BusinessImageLookup: React.FC<{
	business: FormValuesType<BusinessWithSimpleItems>;
	selectImage: (image: string) => void;
	cancel: () => {};
}> = ({ business, selectImage, cancel }) => {
	const [imageURLs, setImageURLs] = useState<string[]>([]);
	const [error, setError] = useState(false);
	const [loading, setLoading] = useState(false);
	useLoading({ message: `Attempting to fetch images from ${business?.website?.value}`, isLoading: loading });
	const [showToast] = useIonToast();

	if (error) {
		showToast({ duration: 1500, color: 'danger', position: 'top', message: 'Unable to find any images' });
	}

	const [trigger] = useLazyGetBusinessImagesQuery();

	const getImagesFromURL = useCallback(
		async (business: Business) => {
			setLoading(true);
			const result = await trigger(business);
			setLoading(false);
			if (result.error) {
				setError(true);
				return;
			}
			const images = result.data;
			if (!images || images.length === 0) {
				setError(true);
			} else {
				setError(false);
				setImageURLs(images);
			}
		},
		[trigger]
	);

	useEffect(() => {
		if (
			business?._id &&
			business?._id?.errors?.length === 0 &&
			business?.website &&
			business?.website?.errors?.length === 0
		) {
			getImagesFromURL({ ...EmptyBusiness, _id: business._id.value, website: business.website.value });
		}
		return () => {
			setLoading(false);
			setError(false);
		};
	}, [business._id, business.website, getImagesFromURL]);

	return (
		<IonPage>
			<Header
				title="Select Image"
				left={
					<IconButton
						icon={<IconClose />}
						onClick={() => {
							cancel();
						}}
					/>
				}
			/>
			<QuirkIonContent>
				<>
					{error && (
						<div className={styles.errorBox}>
							<IconSadFace size={60} />
							<div>Try loading their website and right clicking on images to copy their address</div>
							<a href={business?.website?.value} target="_blank" rel="noreferrer">
								{business?.website?.value}
							</a>
						</div>
					)}
					<div>
						{imageURLs?.length > 0 && (
							<div>
								{imageURLs.map(url => (
									<div key={url}>
										<IonImg onClick={() => selectImage(url)} src={url} />
									</div>
								))}
							</div>
						)}
					</div>
				</>
			</QuirkIonContent>
		</IonPage>
	);
};
